import {
  commonCalloutBoxShadow,
  getBaseButtonStyles,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastHoverFocusStyles,
  baseFabricFocusAfterSelector,
  getInnerBorderStyles,
} from '@ms/acui-common/lib/styling';
import { mergeStyleSets, FontWeights, getTheme, getFocusStyle } from '@fluentui/react/lib/Styling';
import { CoauthorGalleryPalette } from './coauthorGalleryPalette';
import { memoizeFunction } from '@fluentui/react/lib/Utilities';
import { IButtonStyles } from '@fluentui/react';
import { IContextualMenuStyles } from '@fluentui/react/lib/ContextualMenu';
import { memoizeWithResultCheck } from '@ms/acui-memoization';
import { shouldUse2PixelFocusBorder } from '@ms/acui-common/lib/experiments';

export const getOverflowMenuItemStyles = memoizeFunction(
  (palette: CoauthorGalleryPalette): IButtonStyles => {
    const baseButtonStyles: IButtonStyles = getBaseButtonStyles(getTheme());
    const defaultStyles: IButtonStyles = {
      root: {
        height: palette.itemHeight,
        maxHeight: palette.itemHeight,
        minWidth: palette.minCalloutWidth,
        width: '100%',
        maxWidth: palette.maxCalloutWidth,
        background: palette.itemBackgroundColor,
        cursor: 'default',
        padding: 0,
        textAlign: 'left',
      },
      rootDisabled: {
        backgroundColor: 'transparent',
      },
      rootHovered: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
        { backgroundColor: palette.itemBackgroundHoverFocusColor },
      ],
      rootFocused: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
        { backgroundColor: palette.itemBackgroundHoverFocusColor },
        ...(shouldUse2PixelFocusBorder()
          ? [
              {
                [baseFabricFocusAfterSelector]: [
                  getInnerBorderStyles({
                    width: 2,
                    color: palette.focusOutlineColor,
                    gap: -1,
                  }),
                  { outline: 'none' },
                ],
              },
            ]
          : []),
      ],
      rootPressed: { backgroundColor: palette.itemBackgroundPressedColor },
      flexContainer: {
        paddingLeft: palette.itemSidePadding,
        paddingRight: palette.itemSidePadding,
      },
      textContainer: {
        width: '100%',
        overflow: 'hidden',
        marginLeft: 8,
      },
      label: {
        textAlign: 'left',
        color: palette.primaryTextColor,
        fontWeight: FontWeights.semibold,
        margin: 0,
        width: 'auto',
        fontSize: palette.primaryTextSize,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      labelDisabled: {
        color: palette.itemDisabledTextColor,
      },
      description: {
        textAlign: 'left',
        fontSize: palette.secondaryTextSize,
        fontWeight: FontWeights.regular,
        color: palette.secondaryTextColor,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
      },
      descriptionDisabled: {
        color: palette.itemDisabledTextColor,
      },
    };

    return mergeStyleSets(baseButtonStyles, defaultStyles);
  }
);

export const getOverflowStartGroupChatStyles = memoizeFunction(
  (palette: CoauthorGalleryPalette): IButtonStyles => {
    const baseButtonStyles: IButtonStyles = getBaseButtonStyles(getTheme());
    const defaultStyles: IButtonStyles = {
      root: {
        minHeight: palette.actionButtonHeight,
        paddingTop: 4,
        width: '100%',
        background: palette.itemBackgroundColor,
        paddingLeft: palette.itemSidePadding,
        paddingBottom: 4,
        cursor: 'default',
      },
      rootHovered: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
        { backgroundColor: palette.itemBackgroundHoverFocusColor },
      ],
      rootFocused: [
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
        { backgroundColor: palette.itemBackgroundHoverFocusColor },
      ],
      rootPressed: { backgroundColor: palette.itemBackgroundPressedColor },
      flexContainer: {
        justifyContent: 'flex-start',
      },
      label: {
        textAlign: 'left',
        color: palette.secondaryTextColor,
        margin: 0,
        lineHeight: 20,
      },
      textContainer: {
        textAlign: 'left',
      },
    };

    return mergeStyleSets(baseButtonStyles, defaultStyles);
  }
);

export const getDescriptiveOverflowStyles = memoizeFunction(
  (palette: CoauthorGalleryPalette, customStyles?: IButtonStyles): IButtonStyles => {
    const defaultStyles: Partial<IButtonStyles> = {
      root: {
        borderRadius: '50%',
        padding: 0,
        boxSizing: 'border-box',
        ...getFocusStyle(
          getTheme(),
          -5,
          'relative',
          undefined /** highContrastStyle */,
          'transparent',
          palette.focusOutlineColor
        ),
      },
      rootExpanded: {
        backgroundColor: palette.overflowExpandedColor,
      },
    };

    return mergeStyleSets(defaultStyles, customStyles);
  }
);

export const getCoauthorSectionStyle = memoizeFunction((palette: CoauthorGalleryPalette): {
  container: React.CSSProperties;
  item: React.CSSProperties;
} => {
  return {
    container: {
      maxHeight: palette.itemHeight * 7.5,
      overflowY: 'auto',
      overflowX: 'hidden',
      width: 'auto',
      paddingLeft: 0,
      maxWidth: palette.maxCalloutWidth,
      minWidth: palette.minCalloutWidth,
    },
    item: {
      display: 'block',
    },
  };
});

export const getCoauthorSectionDividerStyle = memoizeFunction((palette: CoauthorGalleryPalette) => {
  return {
    divider: {
      color: palette.borderColor,
      margin: '1px 4px 1px 4px',
    },
  };
});

export const getOverflowMenuStyles = memoizeWithResultCheck(
  (palette: CoauthorGalleryPalette, styles?: IContextualMenuStyles): Partial<IContextualMenuStyles> => {
    return mergeStyleSets(
      {
        root: commonCalloutBoxShadow,
        header: {
          paddingLeft: palette.itemSidePadding,
          paddingRight: palette.itemSidePadding,
          paddingBottom: 4,
          paddingTop: 8,
          fontSize: palette.primaryTextSize,
          fontWeight: FontWeights.semibold,
          color: palette.primaryTextColor,
          height: 20,
          boxSizing: 'content-box',
        },
        list: {
          minWidth: palette.minCalloutWidth,
          maxWidth: palette.maxCalloutWidth,
          paddingBottom: 4,
        },
      },
      styles
    );
  }
);
